import { useState, useRef, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Shield, Send, Loader2, AlertTriangle } from 'lucide-react'
import { getPublicPersona, interactWithPersona, getChatHistory, type PersonaData } from '../api/client'

function ErrorPage({ code, title, message }: { code: number; title: string; message: string }) {
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <div className="max-w-sm w-full text-center space-y-6">
        <p className="text-6xl font-bold text-gray-200 dark:text-gray-700">{code}</p>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{message}</p>
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-400 dark:text-gray-500">Powered by IDFY</p>
        </div>
      </div>
    </div>
  )
}

interface Message {
  role: 'user' | 'persona'
  text: string
  status?: string
}

export default function PublicPersona() {
  const { handle } = useParams<{ handle: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const token = searchParams.get('t') || ''

  // ── Client-side validation ────────────────────────────────────────
  // Fail fast before making any API calls
  if (!handle) return <ErrorPage code={404} title="Not Found" message="No persona handle specified." />
  if (!token) return <ErrorPage code={403} title="Access Denied" message="This page requires a valid invite link. Please contact the person who shared this link to request access." />
  if (token.length < 20) return <ErrorPage code={403} title="Invalid Link" message="The invite token is malformed. Please check the link and try again." />

  // Key forces full remount when token or handle changes
  return <ValidatedPersonaPage key={`${handle}-${token}`} handle={handle} token={token} searchParams={searchParams} setSearchParams={setSearchParams} />
}

/** Inner component — only renders after client-side validation passes */
function ValidatedPersonaPage({ handle, token, searchParams, setSearchParams }: {
  handle: string; token: string
  searchParams: URLSearchParams; setSearchParams: ReturnType<typeof useSearchParams>[1]
}) {
  const [persona, setPersona] = useState<PersonaData | null>(null)
  const [loading, setLoading] = useState(true)
  const [errorState, setErrorState] = useState<{ code: number; title: string; message: string } | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const [sessionId] = useState(() => {
    const existing = searchParams.get('s')
    if (existing) return existing
    return 'v-' + Math.random().toString(36).slice(2, 10)
  })

  // Update URL with session ID
  useEffect(() => {
    if (!searchParams.get('s')) {
      const params = new URLSearchParams(searchParams)
      params.set('s', sessionId)
      setSearchParams(params, { replace: true })
    }
  }, [sessionId, searchParams, setSearchParams])

  // Sequential load: validate token → load persona → load history
  useEffect(() => {
    let cancelled = false

    async function init() {
      setLoading(true)
      setErrorState(null)

      // Step 1: Validate token + load persona
      try {
        const p = await getPublicPersona(handle, token)
        if (cancelled) return
        if (!p || !p.id) {
          setErrorState({ code: 404, title: 'Not Found', message: 'This persona does not exist or is not currently active.' })
          setLoading(false)
          return
        }
        setPersona(p)
      } catch (err) {
        if (cancelled) return
        const status = (err as { status?: number })?.status
        if (status === 403) {
          setErrorState({ code: 403, title: 'Link Expired or Revoked', message: 'This invite link is no longer valid. It may have been revoked by the owner or has expired. Please request a new invite link.' })
        } else {
          setErrorState({ code: 404, title: 'Not Found', message: 'This persona does not exist or is not currently active.' })
        }
        setLoading(false)
        return
      }

      // Step 2: Load chat history
      try {
        const history = await getChatHistory(handle, sessionId, token)
        if (cancelled) return
        if (history && history.length > 0) {
          const restored: Message[] = []
          for (const msg of history) {
            restored.push({ role: 'user', text: msg.visitorInput })
            restored.push({ role: 'persona', text: msg.modelOutput, status: msg.responseStatus?.toLowerCase() })
          }
          setMessages(restored)
        }
      } catch (_) {
        // History load failure is non-critical — continue without history
      }

      if (!cancelled) setLoading(false)
    }

    init()
    return () => { cancelled = true }
  }, [handle, token, sessionId])

  useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  // Revalidate token on tab focus — catch revoked tokens
  useEffect(() => {
    const onVisible = async () => {
      if (document.hidden || !persona) return
      try {
        await getPublicPersona(handle, token)
      } catch (err) {
        const status = (err as { status?: number })?.status
        if (status === 403) {
          setPersona(null)
          setErrorState({ code: 403, title: 'Link Expired or Revoked', message: 'This invite link is no longer valid. It may have been revoked by the owner or has expired. Please request a new invite link.' })
        }
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [handle, token, persona])

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || sending) return
    const userMsg = input.trim()
    setInput('')
    setMessages((m) => [...m, { role: 'user', text: userMsg }])
    setSending(true)
    try {
      const r = await interactWithPersona(handle, userMsg, sessionId, token)
      setMessages((m) => [...m, { role: 'persona', text: r.response, status: r.status }])
    } catch (err) {
      const status = (err as { status?: number })?.status
      if (status === 403) {
        setMessages((m) => [...m, { role: 'persona', text: 'Your invite link is no longer valid. Please request a new link.', status: 'error' }])
      } else {
        setMessages((m) => [...m, { role: 'persona', text: 'Something went wrong. Please try again.', status: 'error' }])
      }
    } finally { setSending(false) }
  }

  if (errorState) return <ErrorPage code={errorState.code} title={errorState.title} message={errorState.message} />
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-3">
          <Loader2 size={28} className="animate-spin text-brand-600 mx-auto" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading conversation...</p>
        </div>
      </div>
    )
  }
  if (!persona) return <ErrorPage code={404} title="Not Found" message="This persona does not exist or is not currently active." />

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6">
      {/* Representation header */}
      <div className="card p-6 space-y-3">
        <div className="flex items-center gap-1.5 text-xs text-brand-600 dark:text-brand-400 font-medium uppercase tracking-wider">
          <Shield size={12} /> Authorized Representation
        </div>
        <div className="flex items-start gap-4">
          {(persona.personaPhoto || persona.ownerLogoUrl) && (
            <img src={persona.personaPhoto || persona.ownerLogoUrl || ''} alt={persona.name || ''}
              className="w-14 h-14 rounded-xl object-cover border border-gray-200 dark:border-gray-700 flex-shrink-0" />
          )}
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{persona.name}</h1>
            {persona.ownerDisplayName && <p className="text-sm text-gray-500 dark:text-gray-400">Represents {persona.ownerDisplayName}</p>}
            {persona.publicBio && <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">{persona.publicBio}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          {persona.canHelpWith && persona.canHelpWith.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Can help with</p>
              <div className="flex flex-wrap gap-1.5">
                {persona.canHelpWith.map((t, i) => (
                  <span key={i} className="px-2 py-0.5 rounded text-xs bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">{t}</span>
                ))}
              </div>
            </div>
          )}
          {persona.cannotHelpWith && persona.cannotHelpWith.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Cannot help with</p>
              <div className="flex flex-wrap gap-1.5">
                {persona.cannotHelpWith.map((t, i) => (
                  <span key={i} className="px-2 py-0.5 rounded text-xs bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400">{t}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chat area */}
      <div className="card overflow-hidden">
        <div className="p-4 space-y-3 min-h-[200px] max-h-[60vh] overflow-auto">
          {messages.length === 0 && (
            <p className="text-center text-sm text-gray-400 dark:text-gray-500 py-8">
              Ask a question within the persona's defined scope.
            </p>
          )}
          {messages.map((msg, i) => {
            const isSystem = msg.role === 'persona' && (msg.status === 'refused' || msg.status === 'blocked' || msg.status === 'error' || msg.status === 'paused')
            if (isSystem) {
              return (
                <div key={i} className="flex justify-center py-1">
                  <div className="px-4 py-2 rounded-lg text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 text-center max-w-[85%]">
                    {msg.text}
                  </div>
                </div>
              )
            }
            return (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-brand-600 text-white rounded-br-md'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-bl-md'
                }`}>
                  {msg.text}
                </div>
              </div>
            )
          })}
          {sending && (
            <div className="flex justify-start">
              <div className="px-4 py-2.5 rounded-2xl rounded-bl-md bg-gray-100 dark:bg-gray-700">
                <Loader2 size={16} className="animate-spin text-gray-400" />
              </div>
            </div>
          )}
          <div ref={scrollRef} />
        </div>

        <form onSubmit={sendMessage} className="flex items-center gap-2 p-3 border-t border-gray-100 dark:border-gray-700">
          <input
            type="text" value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a question..."
            className="input flex-1 border-0 focus:ring-0 bg-transparent caret-brand-500"
            disabled={sending} maxLength={2000} autoFocus
          />
          <button type="submit" disabled={sending || !input.trim()} className="p-2 rounded-lg text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-900/30 disabled:opacity-30 transition-colors">
            <Send size={18} />
          </button>
        </form>
      </div>

      <div className="text-center text-xs text-gray-400 dark:text-gray-500 space-y-1 py-4">
        <div className="flex items-center justify-center gap-1.5">
          <AlertTriangle size={10} />
          <span>This persona represents a real {persona.type || 'individual'}. Representation is limited to defined scope.</span>
        </div>
        <p>Some requests may be refused. This experience is powered by <strong>IDFY</strong>.</p>
      </div>
    </div>
  )
}
